import { Badge } from '@workspace/ui/components/badge';
import { Clock, FileText, Inbox } from 'lucide-react';
import { m } from '~/paraglide/messages.js';

interface BusinessRequest {
  id: string;
  name: string;
  requested_at: string;
  requested_scopes: string[];
  status: 'pending' | 'approved' | 'rejected';
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
        <Inbox className="h-7 w-7 text-muted-foreground" />
      </div>
      <h3 className="mt-4 text-sm font-semibold text-foreground">
        {m['developer.business_api_keys.table.empty_title']()}
      </h3>
      <p className="mt-1 max-w-xs text-center text-sm text-muted-foreground">
        {m['developer.business_api_keys.table.empty_message']()}
      </p>
    </div>
  );
}

function RequestCard({ request }: { request: BusinessRequest }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {request.name}
          </span>
        </div>
        <Badge
          variant={
            request.status === 'approved'
              ? 'default'
              : request.status === 'rejected'
                ? 'destructive'
                : 'secondary'
          }
        >
          {request.status === 'pending'
            ? m['developer.business_api_keys.table.status_pending']()
            : request.status === 'approved'
              ? m['developer.business_api_keys.table.status_approved']()
              : m['developer.business_api_keys.table.status_rejected']()}
        </Badge>
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {new Date(request.requested_at).toLocaleDateString()}
        </div>
        <span>{request.requested_scopes.length} scopes requested</span>
      </div>
    </div>
  );
}

export function BusinessApiKeysTable() {
  const requests: BusinessRequest[] = [];

  if (requests.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-3">
      {requests.map((req) => (
        <RequestCard key={req.id} request={req} />
      ))}
    </div>
  );
}
